import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, CircularProgress, Chip,
  ToggleButtonGroup, ToggleButton, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, List, ListItem, ListItemIcon,
  ListItemText, Divider, Alert, Paper, Stack,
} from '@mui/material';
import {
  People as PeopleIcon, PersonAdd as PersonAddIcon, School as SchoolIcon,
  HourglassEmpty as PendingIcon, VideoLibrary as VideoLibraryIcon,
  CheckCircle as PublishedIcon, Quiz as QuizIcon, Assignment as AttemptsIcon,
  Visibility as VisibilityIcon, TrendingUp as TrendingUpIcon, Speed as SpeedIcon,
  MenuBook as VocabIcon, Bookmark as BookmarkIcon, Comment as CommentIcon,
  ThumbUp as ThumbUpIcon, PlayCircle as PlayIcon,
  Upload as UploadIcon, FiberNew as NewIcon, CreditCard as SubIcon,
  Warning as WarningIcon, EmojiEvents as TrophyIcon, ExitToApp as AbandonIcon,
  RemoveRedEye as QuizOpenIcon,
} from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { supabase } from '../lib/supabase';
import {
  analyticsService, OverviewStats, UserGrowthPoint, EngagementMetrics,
  ContentAnalytics, TopCreator, TopWatchedVideo, SubscriptionStats, Granularity,
} from '../services/analyticsService';
import { DateRangeFilter } from '../types';

interface ActivityItem {
  id: string;
  type: 'new_user' | 'quiz_attempt' | 'video_watch' | 'new_video';
  message: string;
  timestamp: Date;
}

const formatNumber = (num: number): string => {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatTimeAgo = (date: Date): string => {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

const ACTIVITY_ICONS: Record<string, React.ReactElement> = {
  new_user: <PersonAddIcon color="primary" />,
  quiz_attempt: <QuizIcon color="secondary" />,
  video_watch: <PlayIcon color="success" />,
  new_video: <UploadIcon color="warning" />,
};

const ACTIVITY_COLORS: Record<string, string> = {
  new_user: '#e3f2fd', quiz_attempt: '#f3e5f5', video_watch: '#e8f5e9', new_video: '#fff3e0',
};

interface StatCardProps { title: string; value: number | string; color: string; icon: React.ReactElement; }

const StatCard: React.FC<StatCardProps> = ({ title, value, color, icon }) => (
  <Card elevation={0} sx={{ height: '100%', borderLeft: 4, borderColor: color, border: '1px solid', borderLeftWidth: 4, borderRightColor: 'divider', borderTopColor: 'divider', borderBottomColor: 'divider' }}>
    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" noWrap>{title}</Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>{typeof value === 'number' ? formatNumber(value) : value}</Typography>
        </Box>
        <Box sx={{ bgcolor: `${color}18`, color, borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {React.cloneElement(icon, { sx: { fontSize: 28 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const SectionLoader: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}><CircularProgress /></Box>
);

const AnalyticsPage: React.FC = () => {
  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(null);
  const [userGrowthData, setUserGrowthData] = useState<UserGrowthPoint[]>([]);
  const [engagementMetrics, setEngagementMetrics] = useState<EngagementMetrics | null>(null);
  const [contentAnalytics, setContentAnalytics] = useState<ContentAnalytics | null>(null);
  const [topCreators, setTopCreators] = useState<TopCreator[]>([]);
  const [topWatchedVideos, setTopWatchedVideos] = useState<TopWatchedVideo[]>([]);
  const [subscriptionStats, setSubscriptionStats] = useState<SubscriptionStats | null>(null);
  const [activityFeed, setActivityFeed] = useState<ActivityItem[]>([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingGrowth, setLoadingGrowth] = useState(true);
  const [loadingEngagement, setLoadingEngagement] = useState(true);
  const [loadingContent, setLoadingContent] = useState(true);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const [dateRange, setDateRange] = useState<DateRangeFilter>({ startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), endDate: new Date() });

  const addActivityItem = useCallback((item: Omit<ActivityItem, 'id' | 'timestamp'>) => {
    setActivityFeed(prev => [{ ...item, id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, timestamp: new Date() }, ...prev].slice(0, 20));
  }, []);

  const fetchOverview = useCallback(async () => { setLoadingOverview(true); const r = await analyticsService.getOverviewStats(); if (r.success) setOverviewStats(r.data); setLoadingOverview(false); }, []);
  const fetchUserGrowth = useCallback(async () => { setLoadingGrowth(true); const r = await analyticsService.getUserGrowthData(dateRange, granularity); if (r.success) setUserGrowthData(r.data); setLoadingGrowth(false); }, [dateRange, granularity]);
  const fetchEngagement = useCallback(async () => { setLoadingEngagement(true); const r = await analyticsService.getEngagementMetrics(); if (r.success) setEngagementMetrics(r.data); setLoadingEngagement(false); }, []);
  const fetchContent = useCallback(async () => {
    setLoadingContent(true);
    const [cr, tr, vr] = await Promise.all([analyticsService.getContentAnalytics(), analyticsService.getTopCreators(10), analyticsService.getTopWatchedVideos(10)]);
    if (cr.success) setContentAnalytics(cr.data); if (tr.success) setTopCreators(tr.data); if (vr.success) setTopWatchedVideos(vr.data);
    setLoadingContent(false);
  }, []);
  const fetchSubscriptions = useCallback(async () => { setLoadingSubscriptions(true); const r = await analyticsService.getSubscriptionStats(); if (r.success) setSubscriptionStats(r.data); setLoadingSubscriptions(false); }, []);

  useEffect(() => { fetchOverview(); fetchEngagement(); fetchContent(); fetchSubscriptions(); }, [fetchOverview, fetchEngagement, fetchContent, fetchSubscriptions]);
  useEffect(() => { fetchUserGrowth(); }, [fetchUserGrowth]);

  useEffect(() => {
    const channel = supabase.channel('admin_activity')
      .on('postgres_changes' as any, { event: 'INSERT', schema: 'public', table: 'profiles' }, (p: any) => { addActivityItem({ type: 'new_user', message: `New user registered: ${p.new?.email || 'Unknown'}` }); })
      .on('postgres_changes' as any, { event: 'INSERT', schema: 'public', table: 'quiz_attempts' }, () => { addActivityItem({ type: 'quiz_attempt', message: 'New quiz attempt submitted' }); })
      .on('postgres_changes' as any, { event: 'INSERT', schema: 'public', table: 'video_progress' }, () => { addActivityItem({ type: 'video_watch', message: 'Video watch progress recorded' }); })
      .on('postgres_changes' as any, { event: 'INSERT', schema: 'public', table: 'videos' }, (p: any) => { addActivityItem({ type: 'new_video', message: `New video uploaded: ${p.new?.title || 'Untitled'}` }); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [addActivityItem]);

  const C = { blue: '#1976d2', lightBlue: '#0288d1', purple: '#7b1fa2', orange: '#f57c00', green: '#388e3c', darkGreen: '#2e7d32', indigo: '#303f9f', red: '#c62828' };

  const overviewCards: StatCardProps[] = overviewStats ? [
    { title: 'Total Users', value: overviewStats.totalUsers, color: C.blue, icon: <PeopleIcon /> },
    { title: 'New Users Today', value: overviewStats.newUsersToday, color: C.lightBlue, icon: <PersonAddIcon /> },
    { title: 'Total Creators', value: overviewStats.totalCreators, color: C.purple, icon: <SchoolIcon /> },
    { title: 'Pending Applications', value: overviewStats.pendingCreatorApplications, color: C.orange, icon: <PendingIcon /> },
    { title: 'Total Videos', value: overviewStats.totalVideos, color: C.green, icon: <VideoLibraryIcon /> },
    { title: 'Published Videos', value: overviewStats.publishedVideos, color: C.darkGreen, icon: <PublishedIcon /> },
    { title: 'Total Quizzes', value: overviewStats.totalQuizzes, color: C.indigo, icon: <QuizIcon /> },
    { title: 'Quiz Attempts Today', value: overviewStats.quizAttemptsToday, color: C.red, icon: <AttemptsIcon /> },
    { title: 'Quiz Abandoned Today', value: overviewStats.quizAbandonedToday, color: C.orange, icon: <AbandonIcon /> },
    { title: 'Quiz Opened Today', value: overviewStats.quizOpenedToday, color: C.lightBlue, icon: <QuizOpenIcon /> },
    { title: 'Vocab Learned Today', value: overviewStats.vocabLearnedToday, color: C.green, icon: <VocabIcon /> },
    { title: 'Vocab Saved Today', value: overviewStats.vocabSavedToday, color: C.darkGreen, icon: <BookmarkIcon /> },
  ] : [];

  const engagementCards = engagementMetrics ? [
    { title: 'Videos Watched', value: formatNumber(engagementMetrics.videosWatched), icon: <VisibilityIcon />, color: '#1976d2' },
    { title: 'Avg Watch Completion', value: `${engagementMetrics.avgWatchCompletion.toFixed(1)}%`, icon: <SpeedIcon />, color: '#0288d1' },
    { title: 'Total Quiz Attempts', value: formatNumber(engagementMetrics.totalQuizAttempts), icon: <AttemptsIcon />, color: '#5c6bc0' },
    { title: 'Quiz Completion Rate', value: `${engagementMetrics.quizCompletionRate.toFixed(1)}%`, icon: <TrendingUpIcon />, color: '#7b1fa2' },
    { title: 'Avg Quiz Score', value: `${engagementMetrics.avgQuizScore.toFixed(1)}%`, icon: <QuizIcon />, color: '#303f9f' },
    { title: 'Vocab Button Clicks', value: formatNumber(engagementMetrics.vocabularyWordsLearned), icon: <VocabIcon />, color: '#388e3c' },
    { title: 'Vocab Words Saved', value: formatNumber(engagementMetrics.vocabularyWordsSaved), icon: <BookmarkIcon />, color: '#2e7d32' },
    { title: 'Comments Posted', value: formatNumber(engagementMetrics.commentsPosted), icon: <CommentIcon />, color: '#f57c00' },
    { title: 'Likes Given', value: formatNumber(engagementMetrics.likesGiven), icon: <ThumbUpIcon />, color: '#c62828' },
  ] : [];

  const sourceTypePieData = contentAnalytics?.videosBySourceType.map((item, idx) => ({ id: idx, value: item.count, label: item.sourceType === 'youtube_shorts' ? 'YouTube Shorts' : item.sourceType === 'local' ? 'Local Upload' : item.sourceType })) || [];
  const genStatus = contentAnalytics?.contentGenerationStatus;
  const subscriptionPieData = subscriptionStats ? [{ id: 0, value: subscriptionStats.free, label: 'Free' }, { id: 1, value: subscriptionStats.premium, label: 'Premium' }] : [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">Analytics Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">Platform performance overview and insights</Typography>
        </Box>
        <Chip label={`New this week: ${overviewStats?.newUsersThisWeek ?? '...'} users`} color="primary" variant="outlined" icon={<TrendingUpIcon />} />
      </Box>

      {loadingOverview ? <SectionLoader /> : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {overviewCards.map(card => <Grid item xs={12} sm={6} md={3} key={card.title}><StatCard {...card} /></Grid>)}
        </Grid>
      )}

      <Card elevation={0} sx={{ mb: 4, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">User Growth</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField type="date" label="Start Date" value={dateRange.startDate?.toISOString().split('T')[0] || ''} onChange={e => setDateRange(p => ({ ...p, startDate: new Date(e.target.value) }))} InputLabelProps={{ shrink: true }} size="small" />
              <TextField type="date" label="End Date" value={dateRange.endDate?.toISOString().split('T')[0] || ''} onChange={e => setDateRange(p => ({ ...p, endDate: new Date(e.target.value) }))} InputLabelProps={{ shrink: true }} size="small" />
              <ToggleButtonGroup value={granularity} exclusive onChange={(_, v) => v && setGranularity(v)} size="small">
                <ToggleButton value="daily">Daily</ToggleButton>
                <ToggleButton value="weekly">Weekly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Box>
          {loadingGrowth ? <SectionLoader /> : userGrowthData.length === 0 ? <Alert severity="info">No user growth data for the selected period.</Alert> : (
            <LineChart
              xAxis={[{ data: userGrowthData.map(d => d.date), scaleType: 'point' }]}
              series={[
                { data: userGrowthData.map(d => d.students), label: 'Students', color: '#1976d2' },
                { data: userGrowthData.map(d => d.creators), label: 'Creators', color: '#f57c00' },
                { data: userGrowthData.map(d => d.total), label: 'Total', color: '#388e3c' },
              ]}
              height={350}
            />
          )}
        </CardContent>
      </Card>

      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Engagement Metrics</Typography>
      {loadingEngagement ? <SectionLoader /> : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {engagementCards.map(card => (
            <Grid item xs={12} sm={6} md={4} key={card.title}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ bgcolor: `${card.color}18`, color: card.color, borderRadius: 2, p: 1, display: 'flex' }}>{React.cloneElement(card.icon, { sx: { fontSize: 24 } })}</Box>
                  <Box><Typography variant="body2" color="text.secondary">{card.title}</Typography><Typography variant="h5" fontWeight="bold">{card.value}</Typography></Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Content Analytics</Typography>
      {loadingContent ? <SectionLoader /> : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Videos by Source Type</Typography>
                {sourceTypePieData.length > 0 ? <PieChart series={[{ data: sourceTypePieData, innerRadius: 40, outerRadius: 100, paddingAngle: 3, cornerRadius: 4 }]} height={250} /> : <Alert severity="info">No video data available.</Alert>}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Content Generation Status</Typography>
                {genStatus ? <BarChart xAxis={[{ scaleType: 'band', data: ['Quiz', 'Vocabulary', 'Subtitles'] }]} series={[{ data: [genStatus.quizGenerated, genStatus.vocabularyGenerated, genStatus.subtitlesGenerated], label: 'Generated', color: '#4caf50' }, { data: [genStatus.quizNotGenerated, genStatus.vocabularyNotGenerated, genStatus.subtitlesNotGenerated], label: 'Not Generated', color: '#ef5350' }]} height={250} /> : <Alert severity="info">No content generation data available.</Alert>}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {!loadingContent && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}><TrophyIcon sx={{ color: '#f57c00' }} /><Typography variant="subtitle1" fontWeight="bold">Top Creators</Typography></Box>
                {topCreators.length > 0 ? (
                  <TableContainer><Table size="small"><TableHead><TableRow><TableCell sx={{ fontWeight: 'bold' }}>#</TableCell><TableCell sx={{ fontWeight: 'bold' }}>Creator</TableCell><TableCell align="right" sx={{ fontWeight: 'bold' }}>Videos</TableCell></TableRow></TableHead><TableBody>
                    {topCreators.map((c, i) => <TableRow key={c.id} hover><TableCell>{i + 1}</TableCell><TableCell>{c.name}</TableCell><TableCell align="right"><Chip label={c.videoCount} size="small" color="primary" variant="outlined" /></TableCell></TableRow>)}
                  </TableBody></Table></TableContainer>
                ) : <Alert severity="info">No creator data available.</Alert>}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}><PlayIcon sx={{ color: '#1976d2' }} /><Typography variant="subtitle1" fontWeight="bold">Top Watched Videos</Typography></Box>
                {topWatchedVideos.length > 0 ? (
                  <TableContainer><Table size="small"><TableHead><TableRow><TableCell sx={{ fontWeight: 'bold' }}>#</TableCell><TableCell sx={{ fontWeight: 'bold' }}>Video</TableCell><TableCell align="right" sx={{ fontWeight: 'bold' }}>Views</TableCell></TableRow></TableHead><TableBody>
                    {topWatchedVideos.map((v, i) => <TableRow key={v.videoId} hover><TableCell>{i + 1}</TableCell><TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</TableCell><TableCell align="right"><Chip label={formatNumber(v.watchCount)} size="small" color="success" variant="outlined" /></TableCell></TableRow>)}
                  </TableBody></Table></TableContainer>
                ) : <Alert severity="info">No watch data available.</Alert>}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><NewIcon sx={{ color: '#c62828' }} /><Typography variant="subtitle1" fontWeight="bold">Real-Time Activity Feed</Typography><Chip label="LIVE" size="small" color="error" sx={{ ml: 'auto', fontWeight: 'bold', fontSize: '0.65rem' }} /></Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Streaming platform events via Supabase Realtime</Typography>
              <Paper variant="outlined" sx={{ maxHeight: 380, overflow: 'auto' }}>
                {activityFeed.length === 0 ? <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">Waiting for real-time events...</Typography></Box> : (
                  <List disablePadding>
                    {activityFeed.map((item, idx) => (
                      <React.Fragment key={item.id}>
                        {idx > 0 && <Divider />}
                        <ListItem sx={{ bgcolor: ACTIVITY_COLORS[item.type], transition: 'background-color 0.3s' }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>{ACTIVITY_ICONS[item.type]}</ListItemIcon>
                          <ListItemText primary={item.message} secondary={formatTimeAgo(item.timestamp)} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}><SubIcon sx={{ color: '#7b1fa2' }} /><Typography variant="subtitle1" fontWeight="bold">Subscription Overview</Typography></Box>
              {loadingSubscriptions ? <SectionLoader /> : subscriptionStats ? (
                <>
                  <PieChart series={[{ data: subscriptionPieData, innerRadius: 35, outerRadius: 80, paddingAngle: 3, cornerRadius: 4 }]} height={200} />
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
                    <Box sx={{ textAlign: 'center' }}><Typography variant="h5" fontWeight="bold">{formatNumber(subscriptionStats.free)}</Typography><Typography variant="caption" color="text.secondary">Free</Typography></Box>
                    <Box sx={{ textAlign: 'center' }}><Typography variant="h5" fontWeight="bold" color="warning.main">{formatNumber(subscriptionStats.premium)}</Typography><Typography variant="caption" color="text.secondary">Premium</Typography></Box>
                  </Box>
                </>
              ) : <Alert severity="info">No subscription data available.</Alert>}
            </CardContent>
          </Card>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}><WarningIcon sx={{ color: '#f57c00' }} /><Typography variant="subtitle1" fontWeight="bold">Expiring Subscriptions (30 days)</Typography></Box>
              {loadingSubscriptions ? <SectionLoader /> : subscriptionStats && subscriptionStats.expiringSoon.length > 0 ? (
                <TableContainer><Table size="small"><TableHead><TableRow><TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell><TableCell align="right" sx={{ fontWeight: 'bold' }}>Expires</TableCell></TableRow></TableHead><TableBody>
                  {subscriptionStats.expiringSoon.map(sub => <TableRow key={sub.id} hover><TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.email}</TableCell><TableCell align="right"><Chip label={new Date(sub.expiresAt).toLocaleDateString()} size="small" color="warning" variant="outlined" /></TableCell></TableRow>)}
                </TableBody></Table></TableContainer>
              ) : <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No subscriptions expiring in the next 30 days.</Typography>}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsPage;
